import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { LineGraph, Scorecard } from '../components/utility/Charts';
import { StoreDateRangeFilter, Caret, TablePagination } from '../components/utility/Utility';
import AnalyticsSourceSelector from '../components/AnalyticsSourceSelector';
import { IdNameMultiSelectFilter, SingleSelectDropdown } from './ProductAnalysis';
import { storeApiBoundsFromMatch, storeMatchRangeLastNCalendarDays } from '../utils/storeDateRange';
import {
  CUSTOM_RANGE_LABEL,
  DEFAULT_RANGE_LABEL,
  RANGE_PRESETS,
  parseViewState,
  resolveRangeFromUrl,
  serializeRangeState
} from '../utils/productAnalysisUrlState';
import '../assets/css/components/program.css';
import '../assets/css/charts.css';
import loadingBuffering from '../assets/images/loading-buffering.gif';

axios.defaults.withCredentials = true;

const API = 'https://api.amplifyloyalty.com/app/klaviyo';
const TABLE_PAGE_SIZE = 50;
const CHILD_PAGE_SIZE = 100;

const TIME_GRAIN_OPTIONS = [
  { id: 'month', label: 'Month' },
  { id: 'day', label: 'Day' },
  { id: 'weekday', label: 'Day of Week' }
];

const LEVEL_OPTIONS = [
  { id: 'campaign', label: 'Campaigns' },
  { id: 'flow', label: 'Flows' }
];

const LEVEL_NAME_HEADER = { campaign: 'Campaign', flow: 'Flow' };
const LEVEL_DOC_TYPE = { campaign: 'campaigns', flow: 'flows' };

const SERIES_METRICS = {
  Recipients: { id: 'Recipients', label: 'Recipients', format: 'count' },
  Delivered: { id: 'Delivered', label: 'Delivered', format: 'count' },
  Opens: { id: 'Opens', label: 'Opens', format: 'count' },
  OpenRate: { id: 'OpenRate', label: 'Open Rate', format: 'percent' },
  Clicks: { id: 'Clicks', label: 'Clicks', format: 'count' },
  ClickRate: { id: 'ClickRate', label: 'Click Rate', format: 'percent' },
  Unsubscribes: { id: 'Unsubscribes', label: 'Unsubscribes', format: 'count' },
  Bounced: { id: 'Bounced', label: 'Bounced', format: 'count' },
  BounceRate: { id: 'BounceRate', label: 'Bounce Rate', format: 'percent' },
  SpamComplaints: { id: 'SpamComplaints', label: 'Spam Complaints', format: 'count' },
  SpamComplaintRate: { id: 'SpamComplaintRate', label: 'Spam Rate', format: 'percent' },
  Conversions: { id: 'Conversions', label: 'Conversions', format: 'decimal' },
  ConversionValue: { id: 'ConversionValue', label: 'Revenue', format: 'money' },
  AOV: { id: 'AOV', label: 'AOV', format: 'money' },
  RevenuePerRecipient: { id: 'RevenuePerRecipient', label: 'Rev / Recipient', format: 'money' }
};
const SERIES_NONE = { id: null, label: 'None' };
const SERIES_METRIC_OPTIONS = Object.values(SERIES_METRICS);

const TABLE_COLUMNS = [
  { id: 'recipients', label: 'Recipients', format: 'count' },
  { id: 'delivered', label: 'Delivered', format: 'count' },
  { id: 'opens_unique', label: 'Opens', format: 'count' },
  { id: 'open_rate', label: 'Open %', format: 'percent' },
  { id: 'clicks_unique', label: 'Clicks', format: 'count' },
  { id: 'click_rate', label: 'Click %', format: 'percent' },
  { id: 'unsubscribes', label: 'Unsubs', format: 'count' },
  { id: 'bounced', label: 'Bounced', format: 'count' },
  { id: 'spam_complaints', label: 'Spam', format: 'count' },
  { id: 'conversions', label: 'Conv.', format: 'decimal' },
  { id: 'conversion_value', label: 'Revenue', format: 'money' },
  { id: 'average_order_value', label: 'AOV', format: 'money' },
  { id: 'revenue_per_recipient', label: 'Rev/Recip', format: 'money' }
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EMPTY_FILTERS = { channels: [], campaign_ids: [], flow_ids: [] };

function formatGrainTick(value, grain) {
  const s = String(value ?? '');
  if (grain === 'month') {
    const [y, m] = s.split('-');
    return `${MONTH_LABELS[parseInt(m, 10) - 1] || m} ${String(y).slice(-2)}`;
  }
  if (grain === 'weekday') return s;
  const p = s.split('-');
  return p.length >= 3 ? `${parseInt(p[1], 10)}/${parseInt(p[2], 10)}` : s;
}

function formatGrainLabel(value, grain) {
  const s = String(value ?? '');
  if (grain === 'month') {
    const [y, m] = s.split('-');
    return `${MONTH_LABELS[parseInt(m, 10) - 1] || m} ${y}`;
  }
  if (grain === 'weekday') return s;
  const p = s.split('-');
  return p.length >= 3 ? `${parseInt(p[1], 10)}/${parseInt(p[2], 10)}/${p[0]}` : s;
}

/** undefined = all (omit filter); [] = none; otherwise restrict to these ids. */
function narrowed(selected, universe) {
  if (!universe.length) return undefined;
  if (selected.length === 0) return [];
  if (selected.length >= universe.length) return undefined;
  return selected;
}

export default function KlaviyoAnalytics() {
  const { getJwtToken, userProfile } = useContext(AuthContext);
  const timezone = userProfile?.store?.settings?.timezone;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlStateRef = useRef(null);
  if (urlStateRef.current === null) urlStateRef.current = parseViewState(searchParams);

  const [activeRange, setActiveRange] = useState(DEFAULT_RANGE_LABEL);
  const [match, setMatch] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const end = new Date();
    end.setDate(end.getDate() - 1);
    return { start, end };
  });
  const seededMatchForTz = useRef(null);
  const [matchReadyTz, setMatchReadyTz] = useState(null);

  const [connected, setConnected] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [candidatesRangeKey, setCandidatesRangeKey] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [timeGrain, setTimeGrain] = useState('day');
  const [tableLevel, setTableLevel] = useState('campaign');
  const [leftMetric, setLeftMetric] = useState('ConversionValue');
  const [rightMetric, setRightMetric] = useState('Opens');
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [error, setError] = useState('');

  const [tableSort, setTableSort] = useState({ sort_by: 'conversion_value', sort_dir: -1 });
  const [tablePage, setTablePage] = useState(1);
  const [table, setTable] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false);
  // { [`campaign:${id}` | `flow:${id}`]: { status, rows } }, valid for `childrenKeyRef`.
  const [children, setChildren] = useState({});
  const [expanded, setExpanded] = useState({});
  const childrenKeyRef = useRef(null);

  const numberFormat = useMemo(() => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }), []);
  const decimalFormat = useMemo(() => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }), []);
  const moneyFormat = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }),
    []
  );
  const moneyTick = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
    []
  );

  const fmt = (kind, v) => {
    const n = Number(v) || 0;
    if (kind === 'money') return moneyFormat.format(n);
    if (kind === 'percent') return `${Math.round(n * 10000) / 100}%`;
    if (kind === 'decimal') return decimalFormat.format(n);
    return numberFormat.format(n);
  };
  const fmtTick = (kind, v) => {
    const n = Number(v) || 0;
    if (kind === 'money') return moneyTick.format(n);
    if (kind === 'percent') return `${Math.round(n * 1000) / 10}%`;
    return numberFormat.format(n);
  };

  useEffect(() => {
    if (!timezone || seededMatchForTz.current === timezone) return;
    seededMatchForTz.current = timezone;
    const fromUrl = resolveRangeFromUrl(urlStateRef.current, timezone);
    if (fromUrl) {
      setActiveRange(fromUrl.activeRange);
      setMatch(fromUrl.match);
    } else {
      setActiveRange(DEFAULT_RANGE_LABEL);
      setMatch(storeMatchRangeLastNCalendarDays(timezone, 7));
    }
    setMatchReadyTz(timezone);
  }, [timezone]);

  useEffect(() => {
    if (!matchReadyTz || matchReadyTz !== timezone) return;
    setSearchParams(serializeRangeState({ activeRange, match, timezone }), { replace: true });
  }, [activeRange, match, timezone, matchReadyTz, setSearchParams]);

  const filterRangeKey = useMemo(() => {
    if (!timezone || !match?.start || !match?.end) return null;
    const { start, end } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    return `${start}|${end}`;
  }, [match, timezone]);

  const request = useMemo(() => {
    if (!timezone || !match?.start || !match?.end || !candidates) {
      return { key: null, params: null };
    }
    const { start, end } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    const params = {
      start,
      end,
      timezone,
      time_grain: timeGrain,
      channels: narrowed(
        filters.channels,
        (candidates.channels || []).map((c) => c.id)
      ),
      campaign_ids: narrowed(filters.campaign_ids, candidates.campaigns || []),
      flow_ids: narrowed(filters.flow_ids, candidates.flows || [])
    };
    return { key: JSON.stringify(params), params };
  }, [match, timezone, timeGrain, filters, candidates]);

  useEffect(() => {
    if (!filterRangeKey || !timezone) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getJwtToken();
        const { start, end } = storeApiBoundsFromMatch(timezone, match.start, match.end);
        const res = await axios.post(
          `${API}/filter-candidates`,
          { start, end, timezone },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (res.data?.connected === false) {
          setConnected(false);
          return;
        }
        setConnected(true);
        const campaigns = res.data.campaigns || [];
        const flows = res.data.flows || [];
        const channels = res.data.channels || [];
        setCandidates({ campaigns, flows, channels });
        // Default to "All" whenever the range changes, matching Meta Ads.
        setFilters({
          channels: channels.map((c) => c.id),
          campaign_ids: campaigns.map((c) => c.id),
          flow_ids: flows.map((f) => f.id)
        });
        setCandidatesRangeKey(filterRangeKey);
      } catch (err) {
        console.log(err);
        if (!cancelled) setError('Failed to load Klaviyo filters.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterRangeKey, match, timezone, getJwtToken]);

  useEffect(() => {
    const { key, params } = request;
    if (!params) return;
    let cancelled = false;
    setLoadingAnalytics(true);
    setError('');
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(`${API}/analytics`, params, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled || request.key !== key) return;
        if (res.data?.connected === false) {
          setConnected(false);
          return;
        }
        setAnalytics(res.data);
      } catch (err) {
        console.log(err);
        if (!cancelled) setError('Failed to load Klaviyo analytics.');
      } finally {
        if (!cancelled) setLoadingAnalytics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request, getJwtToken]);

  useEffect(() => {
    const { params } = request;
    if (!params) return;
    let cancelled = false;
    setLoadingTable(true);
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/table`,
          {
            ...params,
            level: tableLevel,
            page: tablePage,
            pageSize: TABLE_PAGE_SIZE,
            ...tableSort
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        setTable(res.data);
      } catch (err) {
        console.log(err);
        if (!cancelled) setError('Failed to load Klaviyo table.');
      } finally {
        if (!cancelled) setLoadingTable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request, tableLevel, tablePage, tableSort, getJwtToken]);

  useEffect(() => {
    setTablePage(1);
  }, [request.key, tableLevel, tableSort.sort_by, tableSort.sort_dir]);

  // Child rows belong to one request + sort + table level; reset them whenever any changes.
  const childrenKey = `${request.key}|${tableLevel}|${tableSort.sort_by}|${tableSort.sort_dir}`;
  useEffect(() => {
    childrenKeyRef.current = childrenKey;
    setChildren({});
    setExpanded({});
  }, [childrenKey]);

  useEffect(() => {
    const { params } = request;
    if (!params) return;
    const missing = Object.keys(expanded).filter((k) => expanded[k] && !children[k]);
    if (!missing.length) return;
    const key = childrenKey;
    setChildren((prev) => {
      const next = { ...prev };
      for (const k of missing) next[k] = { status: 'loading', rows: [] };
      return next;
    });
    missing.forEach(async (k) => {
      const colon = k.indexOf(':');
      const parentLevel = k.slice(0, colon);
      const id = k.slice(colon + 1);
      let level;
      let parentFields;
      if (parentLevel === 'campaign') {
        level = 'campaign_variation';
        parentFields = { parent_campaign_id: id };
      } else if (parentLevel === 'flow_message') {
        const pipe = id.indexOf('|');
        level = 'flow_message_variation';
        parentFields = {
          parent_flow_id: pipe >= 0 ? id.slice(0, pipe) : id,
          parent_flow_message_id: pipe >= 0 ? id.slice(pipe + 1) : ''
        };
      } else {
        level = 'flow_message';
        parentFields = { parent_flow_id: id };
      }
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/table`,
          {
            ...params,
            level,
            ...parentFields,
            page: 1,
            pageSize: CHILD_PAGE_SIZE,
            ...tableSort
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (childrenKeyRef.current !== key) return;
        setChildren((prev) => ({ ...prev, [k]: { status: 'ready', rows: res.data?.rows || [] } }));
      } catch (err) {
        console.log(err);
        if (childrenKeyRef.current !== key) return;
        setChildren((prev) => ({ ...prev, [k]: { status: 'error', rows: [] } }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, children, childrenKey, getJwtToken]);

  const toggleExpanded = (k) => {
    setChildren((prev) => {
      if (prev[k]?.status !== 'error') return prev;
      const { [k]: _failed, ...rest } = prev;
      return rest;
    });
    setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const sortBy = (id) =>
    setTableSort((s) => ({ sort_by: id, sort_dir: s.sort_by === id ? s.sort_dir * -1 : -1 }));

  const chartData = useMemo(
    () => (Array.isArray(analytics?.series) ? analytics.series : []),
    [analytics?.series]
  );

  const renderMetricCells = (row) =>
    TABLE_COLUMNS.map((col) => (
      <div key={col.id} className="meta-ads-table__cell meta-ads-table__cell--metric">
        {fmt(col.format, row[col.id])}
      </div>
    ));

  const renderStatusDot = (row) => (
    <span
      className={`meta-ads-table__status-dot${
        String(row.status).toLowerCase() === 'live' || String(row.status).toLowerCase() === 'sent'
          ? ' meta-ads-table__status-dot--active'
          : ''
      }`}
      title={row.status || undefined}
    />
  );

  const renderChildRows = (childKey, depth = 1) => {
    const state = children[childKey];
    const depthClass = `meta-ads-table__row--depth-${depth}`;
    if (!state || state.status === 'loading') {
      return (
        <div className={`meta-ads-table__row ${depthClass} meta-ads-table__row--muted klaviyo-table__row`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">Loading…</div>
        </div>
      );
    }
    if (state.status === 'error') {
      return (
        <div className={`meta-ads-table__row ${depthClass} meta-ads-table__row--muted klaviyo-table__row`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">
            Failed to load. Collapse and expand to retry.
          </div>
        </div>
      );
    }
    if (!state.rows.length) {
      return (
        <div className={`meta-ads-table__row ${depthClass} meta-ads-table__row--muted klaviyo-table__row`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">No activity in range</div>
        </div>
      );
    }
    return state.rows.map((row) => {
      // Flow messages: variation dropdown only when the API marked a real A/B test.
      const nestedKey =
        depth === 1 && tableLevel === 'flow' && row.expandable === true
          ? `flow_message:${row.flow_id}|${row.flow_message_id}`
          : null;
      const nestedOpen = nestedKey && !!expanded[nestedKey];
      if (nestedKey) {
        return (
          <div key={row.id}>
            <div className={`meta-ads-table__row ${depthClass} klaviyo-table__row`}>
              <div
                className="meta-ads-table__cell meta-ads-table__cell--name meta-ads-table__cell--name-expandable"
                role="button"
                tabIndex={0}
                aria-expanded={nestedOpen}
                onClick={() => toggleExpanded(nestedKey)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleExpanded(nestedKey);
                  }
                }}
              >
                <span className="meta-ads-table__expander" aria-hidden="true">
                  {nestedOpen ? '▾' : '▸'}
                </span>
                {renderStatusDot(row)}
                <span className="meta-ads-table__name" title={row.name}>{row.name}</span>
              </div>
              {renderMetricCells(row)}
            </div>
            {nestedOpen && renderChildRows(nestedKey, 2)}
          </div>
        );
      }
      return (
        <div key={row.id} className={`meta-ads-table__row ${depthClass} klaviyo-table__row`}>
          <div className="meta-ads-table__cell meta-ads-table__cell--name">
            {renderStatusDot(row)}
            <span className="meta-ads-table__name" title={row.name}>{row.name}</span>
          </div>
          {renderMetricCells(row)}
        </div>
      );
    });
  };

  if (connected === false) return <Navigate to="/app/product-analysis/analytics" replace />;

  const summary = analytics?.summary;
  const filtersDisabled =
    !candidates || candidatesRangeKey !== filterRangeKey || loadingAnalytics || loadingTable;
  const leftOptions = [...SERIES_METRIC_OPTIONS.filter((o) => o.id !== rightMetric), SERIES_NONE];
  const rightOptions = [...SERIES_METRIC_OPTIONS.filter((o) => o.id !== leftMetric), SERIES_NONE];

  const scorecard = (title, kind, value) => (
    <Scorecard
      className="dashboard__scorecard"
      title={title}
      data={loadingAnalytics || !summary ? '—' : fmt(kind, value)}
    />
  );

  const sync = analytics?.sync;
  const syncRunning = sync && ['queued', 'backfilling', 'running'].includes(sync.status);
  const syncProgressLabel = syncRunning
    ? `Backfilling ${
        sync.chunks_total
          ? `${Math.min(100, Math.round(((sync.chunks_processed || 0) / sync.chunks_total) * 100))}%`
          : '…'
      }`
    : null;

  return (
    <div className="product-analysis-scroll">
      <div className="dashboard dashboard--product-analysis">
        <div className="dashboard__filters product-analysis-filters product-analysis-filters--split-rows">
          <div className="product-analysis-filters__stack">
            <div className="product-analysis-filters__left">
              <div className="meta-ads-header-title">
                <AnalyticsSourceSelector current="klaviyo" activeRange={activeRange} match={match} timezone={timezone} />
                {syncProgressLabel && <span className="meta-ads-sync-progress">{syncProgressLabel}</span>}
              </div>
              <div className="date-filter promotions-table-filters__filter-wrapper">
                <StoreDateRangeFilter
                  active={activeRange}
                  setActive={setActiveRange}
                  timezone={timezone}
                  match={match}
                  disabled={filtersDisabled}
                  popoverAlign="start"
                  options={[
                    ...RANGE_PRESETS.map((preset) => ({
                      text: preset.label,
                      action: () => setMatch(preset.resolve(timezone))
                    })),
                    {
                      text: CUSTOM_RANGE_LABEL,
                      action: (customStart, customEnd) => setMatch({ start: customStart, end: customEnd })
                    }
                  ]}
                />
              </div>
            </div>
            <div className="product-analysis-filters__filter-panel product-analysis-filters__filter-panel--klaviyo">
              <IdNameMultiSelectFilter
                label="Channel"
                options={candidates?.channels || []}
                value={filters.channels}
                onChange={(channels) => patchFilters({ channels })}
                emptyText="No channels"
                disabled={filtersDisabled}
              />
              <IdNameMultiSelectFilter
                label="Campaigns"
                options={candidates?.campaigns || []}
                value={filters.campaign_ids}
                onChange={(campaign_ids) => patchFilters({ campaign_ids })}
                showSearch
                searchPlaceholder="Search campaigns"
                emptyText="No campaigns"
                allowEmpty
                disabled={filtersDisabled}
              />
              <IdNameMultiSelectFilter
                label="Flows"
                options={candidates?.flows || []}
                value={filters.flow_ids}
                onChange={(flow_ids) => patchFilters({ flow_ids })}
                showSearch
                searchPlaceholder="Search flows"
                emptyText="No flows"
                allowEmpty
                disabled={filtersDisabled}
              />
            </div>
          </div>
        </div>

        {error && <div className="float-element" style={{ padding: 12 }}>{error}</div>}

        {sync?.status === 'error' && (
          <div className="float-element meta-ads-banner meta-ads-banner--error">
            The last Klaviyo sync failed. Reconnect or re-sync from the{' '}
            <Link to="/app/integrations/klaviyo">integration</Link>.
          </div>
        )}

        <div
          className="dashboard__row"
          style={{ position: 'relative', display: 'flex', alignItems: 'stretch', gap: 16, width: '100%', flexWrap: 'nowrap' }}
        >
          <div
            className="float-element product-analysis-scorecards-wrap"
            style={{ width: '33%', maxWidth: '415px', flexShrink: 0, boxSizing: 'border-box', padding: '20px' }}
          >
            <div className="product-analysis-scorecards">
              {scorecard('Revenue', 'money', summary?.conversion_value)}
              {scorecard('Conversions', 'decimal', summary?.conversions)}
              {scorecard('AOV', 'money', summary?.average_order_value)}
              {scorecard('Delivered', 'count', summary?.delivered)}
              {scorecard('Open Rate', 'percent', summary?.open_rate)}
              {scorecard('Click Rate', 'percent', summary?.click_rate)}
              {scorecard('Unsubscribes', 'count', summary?.unsubscribes)}
              {scorecard('Rev / Recipient', 'money', summary?.revenue_per_recipient)}
            </div>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, position: 'relative', display: 'flex', alignSelf: 'stretch' }}>
            <LineGraph
              width="100%"
              height="100%"
              data={chartData}
              heading={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    <span>Performance over Time</span>
                    <SingleSelectDropdown
                      ariaLabel="Left series"
                      options={leftOptions}
                      value={leftMetric}
                      onChange={setLeftMetric}
                      disabled={loadingAnalytics}
                      triggerClassName="product-analysis-filters__multiselect-trigger--series-left"
                    />
                    <span aria-hidden="true">x</span>
                    <SingleSelectDropdown
                      ariaLabel="Right series"
                      options={rightOptions}
                      value={rightMetric}
                      onChange={setRightMetric}
                      disabled={loadingAnalytics}
                      triggerClassName="product-analysis-filters__multiselect-trigger--series-right"
                    />
                  </div>
                  <SingleSelectDropdown
                    label="Time"
                    ariaLabel="Time dimension"
                    options={TIME_GRAIN_OPTIONS}
                    value={timeGrain}
                    onChange={setTimeGrain}
                    disabled={loadingAnalytics}
                    triggerClassName="product-analysis-filters__multiselect-trigger--time"
                  />
                </div>
              }
              className="float-element product-analysis-line-graph"
              showLegend={false}
              keys={[leftMetric, rightMetric]}
              keyLabels={{
                ...(leftMetric ? { [leftMetric]: SERIES_METRICS[leftMetric].label } : {}),
                ...(rightMetric ? { [rightMetric]: SERIES_METRICS[rightMetric].label } : {})
              }}
              formatter={(value, name) => {
                if (leftMetric && name === SERIES_METRICS[leftMetric].label) {
                  return fmt(SERIES_METRICS[leftMetric].format, value);
                }
                if (rightMetric && name === SERIES_METRICS[rightMetric].label) {
                  return fmt(SERIES_METRICS[rightMetric].format, value);
                }
                return value;
              }}
              yLeftTickFormatter={
                leftMetric ? (v) => fmtTick(SERIES_METRICS[leftMetric].format, v) : undefined
              }
              yRightTickFormatter={
                rightMetric ? (v) => fmtTick(SERIES_METRICS[rightMetric].format, v) : undefined
              }
              tickFormatter={(v) => formatGrainTick(v, timeGrain)}
              labelFormatter={(v) => formatGrainLabel(v, timeGrain)}
            />
          </div>
        </div>

        <section className="product-analysis-table__container" style={{ position: 'relative', width: '100%', marginTop: 20, boxSizing: 'border-box' }}>
          <div className="product-analysis-table meta-ads-table klaviyo-table">
            <div className="klaviyo-table__breakdown">
              <span className="klaviyo-table__breakdown-label">Breakdown</span>
              <button
                type="button"
                className="product-analysis-filters__multiselect-trigger klaviyo-table__breakdown-toggle"
                aria-label="Toggle table breakdown between Campaigns and Flows"
                disabled={loadingTable}
                onClick={() => setTableLevel((level) => (level === 'campaign' ? 'flow' : 'campaign'))}
              >
                {LEVEL_OPTIONS.find((o) => o.id === tableLevel)?.label || 'Campaigns'}
              </button>
            </div>
            <div className="meta-ads-table__scroll">
              <div className="meta-ads-table__row meta-ads-table__row--header klaviyo-table__row">
                <div className="meta-ads-table__cell meta-ads-table__cell--name">
                  {LEVEL_NAME_HEADER[tableLevel]}
                </div>
                {TABLE_COLUMNS.map((col) => {
                  const isActiveSort = tableSort.sort_by === col.id;
                  return (
                    <div
                      key={col.id}
                      className={`meta-ads-table__cell meta-ads-table__cell--metric meta-ads-table__sort${isActiveSort ? ' meta-ads-table__sort--active' : ''}`}
                      onClick={() => sortBy(col.id)}
                    >
                      {col.label}
                      {isActiveSort && (
                        <Caret
                          className="meta-ads-table__heading-caret"
                          direction={tableSort.sort_dir}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {loadingTable && !table ? (
                <div className="meta-ads-table__loading">
                  <img src={loadingBuffering} alt="Loading" width={32} />
                </div>
              ) : !table?.rows?.length ? (
                <div className="meta-ads-table__row meta-ads-table__row--muted klaviyo-table__row">
                  <div className="meta-ads-table__cell meta-ads-table__cell--name">No activity in this range.</div>
                </div>
              ) : (
                <>
                  {table.rows.map((row) => {
                    const childKey = `${tableLevel}:${row.id}`;
                    const canExpand = tableLevel === 'flow' || row.expandable === true;
                    const isOpen = canExpand && !!expanded[childKey];
                    return (
                      <div key={row.id}>
                        <div className="meta-ads-table__row klaviyo-table__row">
                          {canExpand ? (
                            <div
                              className="meta-ads-table__cell meta-ads-table__cell--name meta-ads-table__cell--name-expandable"
                              role="button"
                              tabIndex={0}
                              aria-expanded={isOpen}
                              onClick={() => toggleExpanded(childKey)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleExpanded(childKey);
                                }
                              }}
                            >
                              <span className="meta-ads-table__expander" aria-hidden="true">
                                {isOpen ? '▾' : '▸'}
                              </span>
                              {renderStatusDot(row)}
                              <span className="meta-ads-table__name" title={row.name}>{row.name}</span>
                            </div>
                          ) : (
                            <div className="meta-ads-table__cell meta-ads-table__cell--name">
                              {/* Same width as ▸/▾ so non-A/B campaigns align with expandable rows. */}
                              <span className="meta-ads-table__expander" aria-hidden="true" />
                              {renderStatusDot(row)}
                              <span className="meta-ads-table__name" title={row.name}>{row.name}</span>
                            </div>
                          )}
                          {renderMetricCells(row)}
                        </div>
                        {isOpen && renderChildRows(childKey)}
                      </div>
                    );
                  })}
                  <div className="meta-ads-table__row meta-ads-table__row--totals klaviyo-table__row">
                    <div className="meta-ads-table__cell meta-ads-table__cell--name">Total</div>
                    {TABLE_COLUMNS.map((col) => (
                      <div key={col.id} className="meta-ads-table__cell meta-ads-table__cell--metric">
                        {fmt(col.format, table.totals?.[col.id])}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {table?.page_info && table.page_info.total > TABLE_PAGE_SIZE && (
              <TablePagination
                options={{
                  page: table.page_info.page,
                  limit: table.page_info.page_size,
                  totalDocs: table.page_info.total,
                  hasNextPage: table.page_info.page * table.page_info.page_size < table.page_info.total,
                  hasPrevPage: table.page_info.page > 1
                }}
                docType={LEVEL_DOC_TYPE[tableLevel]}
                previous={() => setTablePage((p) => Math.max(1, p - 1))}
                next={() => setTablePage((p) => p + 1)}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
